import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import { useEffect, useState } from "react";
import { calculateUnitPriceBasingOnKilogramPrice } from "../../model/Materials";
import DetailsDialog, { DetailsProps } from "../DetailsDialog";
import NumberInputField from "../ui/NumberInputField";
import TextInputField from "../ui/TextInputField";
import useMaterialApi, { Material } from "../../api/MaterialApi";
import { PricingGroup, PricingGroupElement } from "../../api/PricingGroupApi";

interface Props extends DetailsProps {
  pricingGroup: PricingGroup;
}

const sortMaterialsByName = (material: Material, anotherMaterial: Material) => material.name.localeCompare(anotherMaterial.name);

const PricingGroupElementDetails = (props: Props) => {
  const materialApi = useMaterialApi();
  const pricingGroup = props.pricingGroup;
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<Material>();
  const [unitProfitMargin, setUnitProfitMargin] = useState<number>();
  const [unitCustomerPrice, setUnitCustomerPrice] = useState<number>();

  const initializeFields = (data: any) => {
    console.info(`### initializeFields: ${JSON.stringify(data)}`);
    setSelectedMaterial(data.material);
  };

  const collectData = () => (pricingGroup && selectedMaterial && unitProfitMargin)
    ? {
        pricingGroup,
        material: selectedMaterial,
        unitProfitMargin,
        unitCustomerPrice
    }
    : null;

  const fetchMaterialsAndSortByName = () => {
    materialApi
      .fetchMaterials()
      .then(data => setMaterials((data as Material[]).sort(sortMaterialsByName)))
      .catch(reason => console.error(`Could not fetch materials: ${reason}`));
  };

  const handleMaterialChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedMaterial = event.target.value as Material;
    setSelectedMaterial(selectedMaterial);
    setUnitCustomerPrice(calculateUnitCustomerPrice(selectedMaterial, unitProfitMargin));
  };

  const handleUnitProfitMarginChange = (unitProfitMargin: number) => {
    setUnitProfitMargin(unitProfitMargin);
    setUnitCustomerPrice(calculateUnitCustomerPrice(selectedMaterial, unitProfitMargin));
  };

  const calculateUnitCustomerPrice = (material: Material|undefined, providedUnitProfitMargin: number|undefined) => {
    const effectiveMaterial = material || selectedMaterial;
    if (effectiveMaterial !== undefined) {
      const effectiveUnitProfitMargin = providedUnitProfitMargin || unitProfitMargin;
      validateMaterialKilogramPriceAndDensity(effectiveMaterial);
      const unitCost = calculateUnitPriceBasingOnKilogramPrice(effectiveMaterial);
      const unitCustomerPrice = (unitCost || 0) + (effectiveUnitProfitMargin || 0);
      console.info(`### unitCustomerPrice: ${unitCustomerPrice}`);
      return Number(unitCustomerPrice.toFixed(2));
    } else {
      return 0;
    }
  };

  const validateMaterialKilogramPriceAndDensity = (material: Material) => {
    if (material.kilogramPrice === undefined && props.reportAlert) {
      props.reportAlert('Materiał nie ma ustawionej ceny za kilogram');
    }
    if (material.density === undefined && props.reportAlert) {
      props.reportAlert('Materiał nie ma ustawionej gęstości');
    }
  };

  const renderFields = (initialData: PricingGroupElement|undefined) => (
    <>
      <h2>Wprowadź wpis do grupy cenowej</h2>
      <TextInputField
        label='Grupa'
        autoFocus
        disabled
        initialValue={pricingGroup?.name}
      />
      { materials && <FormControl>
        <InputLabel>Materiał</InputLabel>
        <Select value={materials.find(material => material.id === selectedMaterial?.id) || null} onChange={handleMaterialChange}>
          {materials.map(material => (<MenuItem key={material.id} value={material as any}>{material.name}</MenuItem>))}
        </Select>
      </FormControl> }
      <NumberInputField
        label='Marża za centymetr [zł]'
        initialValue={initialData?.unitProfitMargin}
        onValueChange={handleUnitProfitMarginChange}
      />
      <NumberInputField
        label='Cena dla klienta za centymetr [zł]'
        initialValue={initialData?.unitCustomerPrice}
        calculatedValue={unitCustomerPrice}
        onValueChange={setUnitCustomerPrice}
      />
    </>
  );

  useEffect(fetchMaterialsAndSortByName, []);

  return (
    <DetailsDialog
      { ...props }
      resource={`pricing-groups/${props.pricingGroup.id}/elements`}
      renderFields={renderFields}
      initializeFields={initializeFields}
      collectData={collectData}
    />
  );
};
export default PricingGroupElementDetails;